import { create } from 'zustand'
import { devtools } from 'zustand/middleware'
import type {} from '@redux-devtools/extension'
import { api, ApiRequestError } from '@/lib/api'
import {
  SectionFlagsResponseSchema,
  type SectionFlag,
} from '@/lib/schemas'

// ===================
// Merge-on-read
//
// The backend rolls up flags per (artifact, sectionKey, reconciliationRunId), so
// two addenda touching the same section produce two rows — and a rare
// concurrent-accept race can even produce duplicate rows for the SAME run. The
// UI only ever wants ONE marker per section, so we merge again at read time:
// sum the item counts and keep the newest row's label/run id (the most recent
// addendum is the most relevant "View changes" target). This is idempotent and
// tolerates the dup-row race without needing a backend transaction.
// ===================

/** One section's worth of flags, collapsed for display. */
export interface MergedSectionFlag {
  sectionKey: string
  /** Summed item_count across every unresolved flag on this section. */
  itemCount: number
  /** Label of the newest contributing flag (the "View changes" source). */
  sourceLabel: string | null
  /** Run id of the newest contributing flag (the "View changes" deep link). */
  reconciliationRunId: string | null
  /** Every underlying flag id — "Mark reviewed" resolves them all. */
  flagIds: string[]
  /** Newest contributing createdAt (ISO). */
  newestCreatedAt: string
}

function mergeInto(target: MergedSectionFlag | undefined, flag: SectionFlag): MergedSectionFlag {
  if (!target) {
    return {
      sectionKey: flag.sectionKey,
      itemCount: flag.itemCount,
      sourceLabel: flag.sourceLabel ?? null,
      reconciliationRunId: flag.reconciliationRunId ?? null,
      flagIds: [flag.id],
      newestCreatedAt: flag.createdAt,
    }
  }
  // ISO timestamps sort lexicographically — newest wins the label/run id.
  const isNewer = flag.createdAt > target.newestCreatedAt
  return {
    sectionKey: target.sectionKey,
    itemCount: target.itemCount + flag.itemCount,
    sourceLabel: isNewer ? flag.sourceLabel ?? null : target.sourceLabel,
    reconciliationRunId: isNewer ? flag.reconciliationRunId ?? null : target.reconciliationRunId,
    flagIds: [...target.flagIds, flag.id],
    newestCreatedAt: isNewer ? flag.createdAt : target.newestCreatedAt,
  }
}

/** Merge a flat flag list into a Map keyed by sectionKey (for one artifact). */
export function mergeFlagsBySection(flags: SectionFlag[]): Map<string, MergedSectionFlag> {
  const map = new Map<string, MergedSectionFlag>()
  for (const flag of flags) {
    map.set(flag.sectionKey, mergeInto(map.get(flag.sectionKey), flag))
  }
  return map
}

/** Merge a bid-wide flag list into a Map keyed by `artifactId:sectionKey`. */
export function mergeFlagsByArtifactSection(flags: SectionFlag[]): Map<string, MergedSectionFlag> {
  const map = new Map<string, MergedSectionFlag>()
  for (const flag of flags) {
    const key = `${flag.artifactId}:${flag.sectionKey}`
    map.set(key, mergeInto(map.get(key), flag))
  }
  return map
}

// ===================
// Store
// ===================

interface SectionFlagsState {
  /** artifactId → unresolved flags (raw, unmerged). */
  flagsByArtifact: Record<string, SectionFlag[]>
  /** bidId → unresolved flags across all the bid's artifacts (raw, unmerged). */
  flagsByBid: Record<string, SectionFlag[]>
  /** flagId → true while a resolve is in flight (dedupes double-clicks). */
  resolving: Record<string, boolean>

  loadFlagsForArtifact: (artifactId: string) => Promise<void>
  loadFlagsForBid: (bidId: string) => Promise<void>
  /**
   * Mark a flag reviewed. Optimistically removes it from both caches. Resolves
   * to `true` on success (a 404 counts — someone else already resolved it, so
   * the removal is already the correct end state); on a real failure it restores
   * ONLY this flag and resolves to `false`. Toasting is the caller's job so a
   * batch resolve can surface a single aggregate error.
   */
  resolveFlag: (flagId: string) => Promise<boolean>

  reset: () => void
}

function removeFlag(list: SectionFlag[] | undefined, flagId: string): SectionFlag[] | undefined {
  if (!list) return list
  const next = list.filter((f) => f.id !== flagId)
  return next.length === list.length ? list : next
}

export const useSectionFlagsStore = create<SectionFlagsState>()(
  devtools(
    (set, get) => ({
      flagsByArtifact: {},
      flagsByBid: {},
      resolving: {},

      loadFlagsForArtifact: async (artifactId: string) => {
        try {
          const res = await api.get(`/artifacts/${artifactId}/section-flags`)
          const { flags } = SectionFlagsResponseSchema.parse(res)
          set((state) => ({
            flagsByArtifact: { ...state.flagsByArtifact, [artifactId]: flags },
          }))
        } catch {
          // Non-blocking background surface — the section is still fully editable
          // without the banner/markers, so a failure here must not toast on every
          // editor open. Swallow; the cache stays empty and nothing is shown.
        }
      },

      loadFlagsForBid: async (bidId: string) => {
        try {
          const res = await api.get(`/bids/${bidId}/section-flags`)
          const { flags } = SectionFlagsResponseSchema.parse(res)
          set((state) => ({
            flagsByBid: { ...state.flagsByBid, [bidId]: flags },
          }))
        } catch {
          // Background surface (see loadFlagsForArtifact) — swallow silently.
        }
      },

      resolveFlag: async (flagId: string): Promise<boolean> => {
        if (get().resolving[flagId]) return true

        // Capture the flag object and the exact cache keys it lives in, so a
        // failure restores ONLY this flag. A whole-map snapshot would resurrect
        // siblings that a concurrent resolve removed in the meantime.
        let removedFlag: SectionFlag | undefined
        const artifactKeys: string[] = []
        for (const [k, v] of Object.entries(get().flagsByArtifact)) {
          const found = v.find((f) => f.id === flagId)
          if (found) {
            removedFlag ??= found
            artifactKeys.push(k)
          }
        }
        const bidKeys: string[] = []
        for (const [k, v] of Object.entries(get().flagsByBid)) {
          const found = v.find((f) => f.id === flagId)
          if (found) {
            removedFlag ??= found
            bidKeys.push(k)
          }
        }

        // Optimistically drop the flag everywhere.
        set((state) => {
          const flagsByArtifact: Record<string, SectionFlag[]> = {}
          for (const [k, v] of Object.entries(state.flagsByArtifact)) {
            flagsByArtifact[k] = removeFlag(v, flagId) ?? v
          }
          const flagsByBid: Record<string, SectionFlag[]> = {}
          for (const [k, v] of Object.entries(state.flagsByBid)) {
            flagsByBid[k] = removeFlag(v, flagId) ?? v
          }
          return {
            flagsByArtifact,
            flagsByBid,
            resolving: { ...state.resolving, [flagId]: true },
          }
        })

        try {
          await api.post(`/section-flags/${flagId}/resolve`, {})
          return true
        } catch (error) {
          // A 404 means the flag is already resolved (someone else got there
          // first) — the optimistic remove is already correct, so keep it gone.
          if (error instanceof ApiRequestError && error.statusCode === 404) {
            return true
          }
          // Restore ONLY this flag, into just the lists it was removed from.
          if (removedFlag) {
            const flag = removedFlag
            set((state) => {
              const flagsByArtifact = { ...state.flagsByArtifact }
              for (const k of artifactKeys) {
                const list = flagsByArtifact[k] ?? []
                if (!list.some((f) => f.id === flagId)) flagsByArtifact[k] = [...list, flag]
              }
              const flagsByBid = { ...state.flagsByBid }
              for (const k of bidKeys) {
                const list = flagsByBid[k] ?? []
                if (!list.some((f) => f.id === flagId)) flagsByBid[k] = [...list, flag]
              }
              return { flagsByArtifact, flagsByBid }
            })
          }
          return false
        } finally {
          set((state) => {
            const next = { ...state.resolving }
            delete next[flagId]
            return { resolving: next }
          })
        }
      },

      reset: () => {
        set({ flagsByArtifact: {}, flagsByBid: {}, resolving: {} })
      },
    }),
    { name: 'section-flags-store' }
  )
)
